import Icons from "assets";
import {
  flattenItems,
  type SidebarAccountAction,
  type SidebarMenuItem,
} from "components/partials/sidebar-menu";
import type { FeatureFlags } from "hooks/api/use-feature-flags";
import { Role } from "types/auth";
import { Route as AppRoute, buildPath, getClientPaths } from "utilities/app-routes";

export const ALL_CLIENTS_WORKSPACE_ID = "all-clients";
export const CLIENT_NAV_SECTION_ID = "client-context-navigation";
export const SIDEBAR_COLLAPSED_STORAGE_KEY = "app.sidebar.collapsed";

export const CLIENT_MENU_IDS = {
  recentCreatives: "menu-recent-creatives",
  campaigns: "menu-campaigns",
  approvalQueue: "menu-approval-queue",
  theme: "menu-theme",
  themeGroup: "menu-theme-group",
  themeBrandIdentity: "menu-theme-brand-identity",
  themePreview: "menu-theme-preview",
  themeConfiguration: "menu-theme-configuration",
  templates: "menu-templates",
  clientAssets: "menu-client-assets",
  profile: "menu-profile",
  collegeProfile: "menu-strategy-college-profile",
  a2a: "menu-programs-a2a",
  a2e: "menu-programs-a2e",
  strategy: "menu-strategy",
  partner: "menu-strategy-partner",
  programs: "menu-strategy-programs",
  brandGuidelines: "menu-strategy-brand-guidelines",
  studentRouter: "menu-student-router",
  adminWorkspace: "menu-admin-workspace",
  campaignTemplates: "menu-campaign-templates",
  campaignWriterRules: "menu-campaign-writer-rules",
} as const;

const CLIENT_USER_MENU_IDS = {
  reviews: "menu-reviews",
  reviewsApprovalQueue: "menu-reviews-approval-queue",
  reviewsCreatives: "menu-reviews-creatives",
  approved: "menu-approved",
} as const;

export type ClientMenuId = (typeof CLIENT_MENU_IDS)[keyof typeof CLIENT_MENU_IDS];
export type ClientUserMenuId = (typeof CLIENT_USER_MENU_IDS)[keyof typeof CLIENT_USER_MENU_IDS];
type ClientPaths = ReturnType<typeof getClientPaths>;

export const GLOBAL_CLIENT_PATHS = getClientPaths({ clientId: undefined });

const TOUCHPOINT_SOURCE_MENU_IDS: Partial<Record<string, ClientMenuId>> = {
  [AppRoute.recentCreatives]: CLIENT_MENU_IDS.recentCreatives,
  [AppRoute.approvalQueue]: CLIENT_MENU_IDS.approvalQueue,
};

type ClientMenuItemDefinition = Omit<SidebarMenuItem, "id" | "to" | "children" | "actions"> & {
  id: ClientMenuId;
  getTo?: (paths: ClientPaths) => string;
  children?: ReadonlyArray<ClientMenuItemDefinition>;
  getActions?: (paths: ClientPaths) => SidebarAccountAction[];
  featureFlag?: string;
  /** Hide this item when the named feature flag is enabled. */
  hideWhenFeatureFlag?: string;
};

export const isClientMenuItemEnabled = (
  item: ClientMenuItemDefinition,
  featureFlags: FeatureFlags,
): boolean => {
  if (item.featureFlag && !featureFlags[item.featureFlag]) {
    return false;
  }

  return !(item.hideWhenFeatureFlag && featureFlags[item.hideWhenFeatureFlag]);
};

type ClientUserSidebarMenuItem = Omit<SidebarMenuItem, "id" | "children"> & {
  id: ClientUserMenuId;
  children?: ClientUserSidebarMenuItem[];
  featureFlag?: string;
};

export const CLIENT_USER_MENU_ITEMS: ReadonlyArray<ClientUserSidebarMenuItem> = [
  {
    id: CLIENT_USER_MENU_IDS.reviews,
    label: "Reviews",
    icon: <Icons.Flag className="size-4" />,
    testId: "nav-link-reviews",
    children: [
      {
        id: CLIENT_USER_MENU_IDS.reviewsApprovalQueue,
        label: "Approval queue",
        icon: <Icons.SidebarNavApprovalQueue className="size-4" />,
        to: buildPath(AppRoute.reviews, AppRoute.approvalQueue),
        testId: "nav-link-reviews-approval-queue",
        featureFlag: "batch-management",
      },
      {
        id: CLIENT_USER_MENU_IDS.reviewsCreatives,
        label: "Creative",
        icon: <Icons.Creatives className="size-4" />,
        to: buildPath(AppRoute.reviews, AppRoute.touchpoints),
        testId: "nav-link-reviews-creatives",
      },
    ],
  },
  {
    id: CLIENT_USER_MENU_IDS.approved,
    label: "Approved",
    icon: <Icons.SidebarNavApproved className="size-4" />,
    to: AppRoute.approved,
    testId: "nav-link-approved",
  },
] as const;

export const CLIENT_MENU_ITEM_DEFINITIONS: ReadonlyArray<ClientMenuItemDefinition> = [
  {
    id: CLIENT_MENU_IDS.profile,
    label: "College profile",
    icon: <Icons.SidebarNavClient className="size-4" />,
    getTo: (paths) => paths.clientProfilePath,
    testId: "secondary-nav-link-profile",
    hideWhenFeatureFlag: "program-briefs",
  },
  {
    id: CLIENT_MENU_IDS.strategy,
    label: "Strategy",
    icon: <Icons.SidebarNavStrategy className="size-4" />,
    testId: "sidebar-link-strategy",
    featureFlag: "program-briefs",
    children: [
      {
        id: CLIENT_MENU_IDS.collegeProfile,
        label: "College profile",
        icon: <Icons.SidebarNavClient className="size-4" />,
        getTo: (paths) => paths.clientCollegeProfilePath,
        testId: "sidebar-link-college-profile",
      },
      {
        id: CLIENT_MENU_IDS.partner,
        label: "Partnership",
        icon: <Icons.SidebarNavPartnership className="size-4" />,
        getTo: (paths) => paths.clientPartnerPath,
        testId: "sidebar-link-partner",
      },
      {
        id: CLIENT_MENU_IDS.programs,
        label: "Programs",
        icon: <Icons.SidebarNavPrograms className="size-4" />,
        testId: "sidebar-link-programs",
        children: [
          {
            id: CLIENT_MENU_IDS.a2a,
            label: "A2A",
            icon: <Icons.SidebarNavA2A className="size-4" />,
            getTo: (paths) => paths.clientProgramsA2APath,
            testId: "sidebar-link-programs-a2a",
          },
          {
            id: CLIENT_MENU_IDS.a2e,
            label: "A2E",
            icon: <Icons.SidebarNavA2E className="size-4" />,
            getTo: (paths) => paths.clientProgramsA2EPath,
            testId: "sidebar-link-programs-a2e",
          },
        ],
      },
      {
        id: CLIENT_MENU_IDS.brandGuidelines,
        label: "Brand guidelines",
        icon: <Icons.SidebarNavBrandGuidelines className="size-4" />,
        getTo: (paths) => paths.clientBrandGuidelinesPath,
        testId: "sidebar-link-brand-guidelines",
      },
    ],
  },
  {
    id: CLIENT_MENU_IDS.studentRouter,
    label: "Student router",
    icon: <Icons.SidebarNavStudentRouter className="size-4" />,
    getTo: (paths) => paths.clientStudentRouterPath,
    testId: "sidebar-link-student-router",
    featureFlag: "student-router",
  },
  {
    id: CLIENT_MENU_IDS.clientAssets,
    label: "Client assets",
    icon: <Icons.College className="size-4" />,
    testId: "sidebar-link-client-assets",
    children: [
      {
        id: CLIENT_MENU_IDS.recentCreatives,
        label: "Recent creatives",
        icon: <Icons.SidebarNavRecentCreatives className="size-4" />,
        getTo: (paths) => paths.clientRecentCreativesPath,
        testId: "sidebar-link-recent-creatives",
      },
      {
        id: CLIENT_MENU_IDS.campaigns,
        label: "Campaigns",
        icon: <Icons.SidebarNavCampaigns className="size-4" />,
        getTo: (paths) => paths.clientCampaignsPath,
        testId: "secondary-nav-link-campaigns",
      },
      {
        id: CLIENT_MENU_IDS.approvalQueue,
        label: "Approval queue",
        icon: <Icons.SidebarNavApprovalQueue className="size-4" />,
        getTo: (paths) => paths.approvalQueuePath,
        testId: "secondary-nav-link-approval-queue",
        featureFlag: "batch-management",
      },
      {
        id: CLIENT_MENU_IDS.theme,
        label: "Theme",
        icon: <Icons.SidebarNavTheme className="size-4" />,
        getTo: (paths) => paths.clientThemePath,
        testId: "sidebar-link-theme",
        hideWhenFeatureFlag: "theme-configuration",
      },
      {
        id: CLIENT_MENU_IDS.themeGroup,
        label: "Theme",
        icon: <Icons.SidebarNavTheme className="size-4" />,
        testId: "sidebar-link-theme-group",
        featureFlag: "theme-configuration",
        children: [
          {
            id: CLIENT_MENU_IDS.themeBrandIdentity,
            label: "Brand identity",
            icon: <Icons.SidebarNavBrandIdentity className="size-4" />,
            getTo: (paths) => paths.clientThemeBrandIdentityPath,
            testId: "sidebar-link-theme-brand-identity",
          },
          {
            id: CLIENT_MENU_IDS.themePreview,
            label: "Preview",
            icon: <Icons.SidebarNavPreview className="size-4" />,
            getTo: (paths) => paths.clientThemePreviewPath,
            testId: "sidebar-link-theme-preview",
          },
          {
            id: CLIENT_MENU_IDS.themeConfiguration,
            label: "Configuration",
            icon: <Icons.SidebarNavConfiguration className="size-4" />,
            getTo: (paths) => paths.clientThemeConfigurationPath,
            testId: "sidebar-link-theme-configuration",
          },
        ],
      },
      {
        id: CLIENT_MENU_IDS.templates,
        label: "Creative templates",
        icon: <Icons.SidebarNavTemplates className="size-4" />,
        getTo: (paths) => paths.clientProgramTemplatesPath,
        testId: "sidebar-link-templates",
      },
    ],
  },
  {
    id: CLIENT_MENU_IDS.adminWorkspace,
    label: "Admin workspace",
    icon: <Icons.SidebarNavAdminWorkspace className="size-4" />,
    testId: "sidebar-link-admin-workspace",
    hasDividerBefore: true,
    children: [
      {
        id: CLIENT_MENU_IDS.campaignTemplates,
        label: "Campaign briefs",
        icon: <Icons.SidebarNavCampaignTemplates className="size-4" />,
        getTo: () => GLOBAL_CLIENT_PATHS.clientCampaignTemplatesPath,
        testId: "sidebar-link-campaign-templates",
        featureFlag: "program-briefs",
      },
      {
        id: CLIENT_MENU_IDS.campaignWriterRules,
        label: "Campaign Writer rules",
        icon: <Icons.CampaignWriter className="size-4" />,
        getTo: () => GLOBAL_CLIENT_PATHS.clientCampaignWriterRulesPath,
        testId: "sidebar-link-campaign-writer-rules",
      },
    ],
  },
];

/** Opening one of these while a client is selected folds Admin workspace, keeping the sidebar short. */
const ADMIN_WORKSPACE_FOLDING_GROUP_IDS: ReadonlyArray<ClientMenuId> = [
  CLIENT_MENU_IDS.strategy,
  CLIENT_MENU_IDS.clientAssets,
];

const NESTED_GROUP_IDS: ReadonlyArray<ClientMenuId> = [
  CLIENT_MENU_IDS.programs,
  CLIENT_MENU_IDS.themeGroup,
];

const CLIENT_MENU_GROUPS = flattenItems(CLIENT_MENU_ITEM_DEFINITIONS).filter(
  (item) => !!item.children?.length,
);

/** Groups the user has opened (`true`) or folded (`false`); a group missing here keeps its default. */
export type ClientMenuExpansion = Partial<Record<string, boolean>>;

/**
 * Folded until opened: the nested groups, and Admin workspace when a client is selected — there it
 * sits below the client's own groups and would only push them off screen.
 */
const isGroupFoldedByDefault = (group: ClientMenuItemDefinition, hasClient: boolean) =>
  NESTED_GROUP_IDS.includes(group.id) ||
  (hasClient && group.id === CLIENT_MENU_IDS.adminWorkspace);

export const getCollapsedClientMenuItemIds = (
  expansion: ClientMenuExpansion,
  hasClient: boolean,
): string[] =>
  CLIENT_MENU_GROUPS.filter(
    (group) => !(expansion[group.id] ?? !isGroupFoldedByDefault(group, hasClient)),
  ).map((group) => group.id);

/**
 * Records a folded-by-default group holding the current page as opened, as if the user had opened it,
 * so the highlighted row is never hidden and the group stays open after navigating elsewhere. A group
 * the user already folded or opened keeps that choice. Returns `expansion` itself when nothing changes.
 */
export const getExpansionWithActiveGroupOpened = (
  expansion: ClientMenuExpansion,
  hasClient: boolean,
  activeItemId: ClientMenuId | undefined,
): ClientMenuExpansion => {
  const activeGroup = CLIENT_MENU_GROUPS.find(
    (group) =>
      expansion[group.id] === undefined &&
      isGroupFoldedByDefault(group, hasClient) &&
      flattenItems(group.children ?? []).some((item) => item.id === activeItemId),
  );

  return activeGroup ? { ...expansion, [activeGroup.id]: true } : expansion;
};

export const getNextClientMenuExpansion = (
  expansion: ClientMenuExpansion,
  itemId: string,
  isExpanded: boolean,
  hasClient: boolean,
): ClientMenuExpansion => {
  const foldsAdminWorkspace =
    hasClient && isExpanded && ADMIN_WORKSPACE_FOLDING_GROUP_IDS.some((id) => id === itemId);

  return {
    ...expansion,
    [itemId]: isExpanded,
    ...(foldsAdminWorkspace && { [CLIENT_MENU_IDS.adminWorkspace]: false }),
  };
};

const CLIENT_SECTION_PATHS = [
  AppRoute.recentCreatives,
  AppRoute.campaigns,
  AppRoute.programs,
  AppRoute.approvalQueue,
  AppRoute.theme,
  AppRoute.programTemplates,
  AppRoute.profile,
  AppRoute.collegeProfile,
  AppRoute.partner,
  AppRoute.brandGuidelines,
  AppRoute.studentRouter,
] as const;

type ClientSection = (typeof CLIENT_SECTION_PATHS)[number];

export const ACCOUNT_ACTIONS: SidebarAccountAction[] = [
  {
    icon: <Icons.Logout className="size-4" />,
    label: "Logout",
    testId: "router-link-logout",
    to: AppRoute.logout,
    onClick: () => {},
  },
];

const ROLE_LABELS: Record<Role, string> = {
  [Role.ROLE_ACCOUNT_MANAGER]: "Account manager",
  [Role.ROLE_CAMPAIGN_MANAGER]: "Campaign manager",
  [Role.ROLE_CLIENT_ADMIN]: "Client admin",
  [Role.ROLE_CLIENT_USER]: "Client user",
  [Role.ROLE_CONTRACTOR_USER]: "Contractor user",
  [Role.ROLE_PRODUCT_ADMIN]: "Product admin",
  [Role.ROLE_STAFF_USER]: "Staff user",
  [Role.ROLE_SUPER_ADMIN]: "Super admin",
};

export const formatRole = (role?: string) => {
  if (!role) {
    return "User";
  }

  return ROLE_LABELS[role as Role] ?? "User";
};

type AccountSubtitleUser = {
  client?: { name?: string };
  roles: Role[];
};

export const getAccountSubtitle = (user: AccountSubtitleUser, isClientUser: boolean) => {
  const clientName = user.client?.name?.trim();

  if (isClientUser && clientName) {
    return clientName;
  }

  return formatRole(user.roles[0]);
};

export const getClientSectionFromPathname = (pathname: string): ClientSection | undefined => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== AppRoute.clients.replace("/", "") || !segments[1]) {
    return undefined;
  }

  const section = segments[2];
  if (!section || !CLIENT_SECTION_PATHS.includes(section as ClientSection)) {
    return undefined;
  }

  return section as ClientSection;
};

export const getActiveClientMenuItemId = (
  pathname: string,
  paths: ClientPaths,
  featureFlags: FeatureFlags,
  isProgramTemplate?: boolean,
  touchpointSource?: string,
): ClientMenuId | undefined => {
  const findActiveId = (
    items: ReadonlyArray<ClientMenuItemDefinition>,
  ): ClientMenuId | undefined => {
    for (const item of items) {
      if (!isClientMenuItemEnabled(item, featureFlags)) continue;
      if (item.getTo && pathname.startsWith(item.getTo(paths))) {
        return item.id;
      }
      if (item.children) {
        const activeChildId = findActiveId(item.children);
        if (activeChildId) return activeChildId;
      }
    }
    return undefined;
  };

  // When navigated from a source list to a touchpoint, keep that source highlighted.
  if (touchpointSource && pathname.includes("/creative/")) {
    const sourceMenuId = TOUCHPOINT_SOURCE_MENU_IDS[touchpointSource];

    if (sourceMenuId) {
      return sourceMenuId;
    }
  }

  // When navigated to a program-template touchpoint, keep "Creative templates" highlighted.
  if (isProgramTemplate && pathname.includes("/creative/")) {
    return CLIENT_MENU_IDS.templates;
  }

  const activeId = findActiveId(CLIENT_MENU_ITEM_DEFINITIONS);

  if (isProgramTemplate && activeId === CLIENT_MENU_IDS.campaigns) {
    return CLIENT_MENU_IDS.templates;
  }

  return activeId;
};

export const getActiveClientUserMenuItemId = (pathname: string): ClientUserMenuId | undefined => {
  for (const item of flattenItems(CLIENT_USER_MENU_ITEMS)) {
    if (typeof item.to === "string" && pathname.startsWith(item.to)) return item.id;
  }

  return undefined;
};
